import { collection, getDocs, orderBy, query, onSnapshot, doc, where, limit, setDoc, Unsubscribe } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from './firebaseService';
import { compressImage } from '../utils/imageCompression';
import type {
  Article, ArticleExternalLink, ArticleImage, ArticleLink, ArticleYouTubeVideo,
  ArticleWheelConfig, ArticleWheelEntry,
} from '../types';

export function subscribeToArticles(callback: (articles: Article[]) => void): Unsubscribe {
  const q = query(collection(db, 'articles'), orderBy('created_at', 'desc'));
  return onSnapshot(q, (snap) => {
    const articles = snap.docs.map(d => ({ id: d.id, ...d.data() } as Article));
    callback(articles);
  }, () => callback([]));
}

/**
 * Abonner på publiserte versjonsnotater (release_notes), nyeste først.
 * Driver in-app «Hva er nytt». Feil/permission-denied → tom liste (fail-soft).
 */
export function subscribeToReleaseNotes(callback: (notes: Article[]) => void, max = 30): Unsubscribe {
  const q = query(
    collection(db, 'articles'),
    where('type', '==', 'release_notes'),
    where('status', '==', 'published'),
    orderBy('published_at', 'desc'),
    limit(max)
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as Article)));
  }, () => callback([]));
}

export async function fetchArticles(): Promise<Article[]> {
  const snap = await getDocs(query(collection(db, 'articles'), orderBy('created_at', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Article));
}

export async function createArticle(title: string): Promise<{ ok: boolean; articleId: string }> {
  const callable = httpsCallable<{ title: string }, { ok: boolean; articleId: string }>(functions, 'createArticle');
  const result = await callable({ title });
  return result.data;
}

export async function updateArticle(opts: {
  articleId: string;
  title?: string;
  content?: string;
  summary?: string;
  images?: ArticleImage[];
  distribute_via_email?: boolean;
  slug?: string;
  linked_content?: ArticleLink[];
  external_links?: ArticleExternalLink[];
  youtube_videos?: ArticleYouTubeVideo[];
  type?: 'blog' | 'release_notes';
  version?: string;
  impact?: 'major' | 'minor';
}): Promise<{ ok: boolean; articleId: string }> {
  const callable = httpsCallable<typeof opts, { ok: boolean; articleId: string }>(functions, 'updateArticle');
  const result = await callable(opts);
  return result.data;
}

export async function fetchExternalLinkMetadata(url: string): Promise<{
  ok: boolean;
  reason?: string;
  data?: { title: string; url: string };
}> {
  const callable = httpsCallable<{ url: string }, { ok: boolean; reason?: string; data?: { title: string; url: string } }>(
    functions,
    'fetchExternalLinkMetadata'
  );
  const result = await callable({ url });
  return result.data;
}

export async function publishArticle(articleId: string): Promise<{
  ok: boolean;
  articleId: string;
  emailSent: number;
  emailFailed: number;
}> {
  const callable = httpsCallable<{ articleId: string }, {
    ok: boolean; articleId: string; emailSent: number; emailFailed: number;
  }>(functions, 'publishArticle');
  const result = await callable({ articleId });
  return result.data;
}

export async function deleteArticle(articleId: string): Promise<{ ok: boolean }> {
  const callable = httpsCallable<{ articleId: string }, { ok: boolean }>(functions, 'deleteArticle');
  const result = await callable({ articleId });
  return result.data;
}

export async function uploadArticleImage(articleId: string, file: File): Promise<string> {
  const { file: compressed } = await compressImage(file);
  const safeName = compressed.name.replace(/\s+/g, '_');
  const fileRef = ref(storage, `articles/${articleId}/${Date.now()}_${safeName}`);
  const snap = await uploadBytes(fileRef, compressed);
  return getDownloadURL(snap.ref);
}

export async function triggerArticleGeneration(customTopic?: string, articleAngle?: string): Promise<{
  ok: boolean;
  articleId: string;
  title: string;
  theme: string;
  season: string;
  youtubeCount: number;
  hasImage: boolean;
}> {
  const callable = httpsCallable<{ customTopic?: string; articleAngle?: string }, {
    ok: boolean; articleId: string; title: string; theme: string; season: string; youtubeCount: number; hasImage: boolean;
  }>(functions, 'triggerArticleGeneration');
  const payload = {
    ...(customTopic ? { customTopic } : {}),
    ...(articleAngle ? { articleAngle } : {}),
  };
  const result = await callable(payload);
  return result.data;
}

export async function searchContentForArticle(searchQuery: string, type: 'route' | 'spot'): Promise<ArticleLink[]> {
  const callable = httpsCallable<{ query: string; type: string }, { ok: boolean; results: ArticleLink[] }>(
    functions, 'searchContentForArticle'
  );
  const result = await callable({ query: searchQuery, type });
  return result.data.results;
}

export async function generateArticleImageManual(articleId: string, style: 'morsom' | 'nostalgisk'): Promise<{
  ok: boolean;
  image: ArticleImage;
  images?: ArticleImage[];
}> {
  const callable = httpsCallable<{ articleId: string; style: string }, {
    ok: boolean; image: ArticleImage; images?: ArticleImage[];
  }>(functions, 'generateArticleImageManual', { timeout: 180000 });
  const result = await callable({ articleId, style });
  return result.data;
}

export async function sendArticleCampaign(articleId: string): Promise<{
  ok: boolean;
  emailSent: number;
  emailFailed: number;
}> {
  const callable = httpsCallable<{ articleId: string }, {
    ok: boolean; articleId: string; emailSent: number; emailFailed: number;
  }>(functions, 'sendArticleCampaign', { timeout: 300000 });
  const result = await callable({ articleId });
  return result.data;
}

/**
 * Runtime AI-skjermbilder: ber backend åpne appen i hodeløs Chromium og ta
 * desktop+mobil-skjermbilder, som legges til artikkelens bilder.
 */
export async function captureAppScreenshots(articleId: string, url?: string): Promise<{
  ok: boolean; added: number; images: ArticleImage[];
}> {
  const callable = httpsCallable<{ articleId: string; url?: string }, { ok: boolean; added: number; images: ArticleImage[] }>(
    functions, 'captureAppScreenshots', { timeout: 180000 }
  );
  const result = await callable({ articleId, ...(url ? { url } : {}) });
  return result.data;
}

// ── Hurtiggodkjenning ──────────────────────────────────────────────────────

export type ReviewArticleAction = 'publish_now' | 'approve_scheduled' | 'later' | 'reject';

/**
 * Ett-trykks behandling av et årshjul-utkast. `publish_now` kjører hele
 * publiseringsløypa (oversettelse til alle språk + eventuell e-postkampanje).
 */
export async function reviewArticle(
  articleId: string,
  action: ReviewArticleAction,
  scheduledYmd?: string,
  /** Kun for 'later': antall dager før saken kommer tilbake i køen. Default 3. */
  snoozeDays?: number,
): Promise<{ ok: boolean; action: string; emailSent?: number; emailFailed?: number; snoozedUntil?: string }> {
  const callable = httpsCallable<
    { articleId: string; action: string; scheduledYmd?: string; snoozeDays?: number },
    { ok: boolean; action: string; emailSent?: number; emailFailed?: number; snoozedUntil?: string }
  >(functions, 'reviewArticle', { timeout: 300000 });
  const result = await callable({
    articleId,
    action,
    ...(scheduledYmd ? { scheduledYmd } : {}),
    ...(snoozeDays ? { snoozeDays } : {}),
  });
  return result.data;
}

/**
 * Hvor mange som får e-post ved publisering. Vises i klartekst på publiser-knappen —
 * ett trykk skal aldri sende masseutsendelse uten at tallet står der.
 */
export async function countArticleEmailRecipients(): Promise<number | null> {
  try {
    const callable = httpsCallable<Record<string, never>, { ok: boolean; count: number | null }>(
      functions, 'countArticleEmailRecipients'
    );
    const result = await callable({});
    return result.data.count;
  } catch {
    return null;
  }
}

/** Én artikkel, live. Brukes av hurtiggodkjenning så «allerede behandlet» oppdages. */
export function subscribeToArticle(
  articleId: string,
  callback: (article: Article | null) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, 'articles', articleId),
    (snap) => callback(snap.exists() ? ({ id: snap.id, ...snap.data() } as Article) : null),
    () => callback(null),
  );
}

// ── AI-forbruk ─────────────────────────────────────────────────────────────

export interface AiUsageFeature {
  feature: string;
  calls: number;
  promptTokens: number;
  outputTokens: number;
  thoughtTokens: number;
  usd: number;
  nok: number;
}

export interface AiUsage {
  ok: boolean;
  days: number;
  features: AiUsageFeature[];
  byDay: Record<string, number>;
  /** Faktisk GCP-forbruk fra budsjettvarslingen. null til første melding kommer. */
  gcp: { cost: number; budget: number; currency: string; updatedAt: string | null } | null;
  totalUsd: number;
  totalNok: number;
  /** Kursen som ble brukt — vises i panelet så tallet kan etterprøves. */
  usdToNok: number;
  /** Alltid true — enhetsprisene er hardkodet. Fasit er Cloud Billing. */
  estimated: boolean;
}

export async function getAiUsage(days = 30): Promise<AiUsage> {
  const callable = httpsCallable<{ days: number }, AiUsage>(functions, 'getAiUsage', { timeout: 60000 });
  return (await callable({ days })).data;
}

// ── Årshjul ────────────────────────────────────────────────────────────────

const WHEEL_DOC = doc(db, 'system', 'article_wheel');

export function subscribeToArticleWheel(
  callback: (wheel: ArticleWheelConfig | null) => void,
): Unsubscribe {
  return onSnapshot(
    WHEEL_DOC,
    (snap) => {
      if (!snap.exists()) return callback({ enabled: false, entries: [] });
      const d = snap.data() as Partial<ArticleWheelConfig>;
      callback({ ...d, enabled: d.enabled === true, entries: Array.isArray(d.entries) ? d.entries : [] });
    },
    () => callback(null),
  );
}

/**
 * Lagre hjulet. Skriver direkte (reglene tillater kun admin på system/*), samme
 * mønster som de andre config-panelene. Backend sanitizer det den leser, så en
 * feilaktig håndredigering kan aldri velte cronen.
 */
export async function saveArticleWheel(
  patch: { enabled?: boolean; entries?: ArticleWheelEntry[] },
): Promise<void> {
  await setDoc(WHEEL_DOC, { ...patch, updatedAt: new Date().toISOString() }, { merge: true });
}

export async function seedArticleWheel(): Promise<{ ok: boolean; entries: number; enabled: boolean }> {
  const callable = httpsCallable<Record<string, never>, { ok: boolean; entries: number; enabled: boolean }>(
    functions, 'seedArticleWheel'
  );
  return (await callable({})).data;
}

/** Kjør årshjulet nå. dryRun=true (default) rapporterer uten å skrive noe. */
export async function runArticleWheelNow(dryRun = true): Promise<{
  ok: boolean; skipped?: string; entries: number; due: number; created: number;
  wouldCreate?: { id: string; title: string; occasionYmd: string }[];
  deferred?: string[];
}> {
  const callable = httpsCallable<{ dryRun: boolean }, never>(functions, 'runArticleWheelNow', { timeout: 120000 });
  return (await callable({ dryRun })).data as never;
}

export async function removeArticleImage(imageUrl: string): Promise<void> {
  try {
    // Extract storage path from Firebase URL
    const url = new URL(imageUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
    if (pathMatch) {
      const storagePath = decodeURIComponent(pathMatch[1]);
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);
    }
  } catch (e) {
    console.warn('removeArticleImage: Could not delete from storage:', e);
  }
}
